import { useMemo, useState } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    getPaginationRowModel,
    flexRender,
    type ColumnDef,
    type SortingState,
} from '@tanstack/react-table';
import { Button, CopyableId } from '@/kit';
import { useLevelEntities } from '../../../../hooks/useLevelEntities';
import { ContentLoading } from '../../../../components/shared/ContentLoading';
import { Gate } from '../../../../components/permissions/Gate';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../../../components/ui/table';
import { DataTableToolbar } from '../../../../components/shared/DataTableToolbar';
import { DataTablePagination } from '../../../../components/shared/DataTablePagination';
import { SortableHeader } from '../../../../components/shared/SortableHeader';
import { globalFilterFn } from '../../../../lib/dataTable';
import { formatDateMx } from '../../../../lib/dateHelpers';
import { LevelEntityForm } from './LevelEntityForm';
import { LevelEntityRowActions } from './LevelEntityRowActions';
import type {
    LevelEntityListItemLocal,
    LevelNameLocal,
} from '../../../../api/types/LevelEntityLocal';

interface Props {
    /** tenantId del DRILL-DOWN (param de ruta). Toda la data se consume contra ESTE tenant. */
    tenantId: string;
    /** Nivel a listar/gestionar (LEVEL_1/2/3). */
    level: LevelNameLocal;
    /**
     * Nombre LEGIBLE del nivel, resuelto desde la config de la APLICACIÓN del tenant. Es lo que se
     * muestra en encabezados/botones/labels — NUNCA "LEVEL_1" crudo (regla anti-nombres/anti-UUID).
     * Hoy se inyecta por prop/route: TD-WEBAPP-023 (no hay endpoint tenant→app→displayName aún).
     */
    levelDisplayName: string;
    /** DEC-RBAC-027: si el nivel es BYOID (externo), el form de alta pide el `id`. Default `false`. */
    requireExternalId?: boolean;
    /**
     * Padre del nivel inmediato superior. OBLIGATORIO para listar/crear en LEVEL_2/LEVEL_3 (cada padre
     * es una sub-lista). Ausente para LEVEL_1. El caller (selector de padre) lo provee.
     */
    parentId?: string;
}

/**
 * Pantalla de nivel GENÉRICA: lista/crea/edita/borra las entidades de un `level` de un tenant. Misma
 * composición que `PlatformRetailersTable` (useReactTable + DataTableToolbar/Pagination +
 * SortableHeader + kebab por fila + modal de alta), pero parametrizada por `level` y mostrando el
 * `levelDisplayName` de la app del tenant en lugar de un recurso hardcodeado. Coexiste con las
 * pantallas bespoke de retailer/store/group (no las reemplaza todavía).
 *
 * Diferencias de contrato vs retailers: el list de nivel NO pagina por cursor (devuelve `{ items }`),
 * así que se usa un `useQuery` plano (no `useInfiniteQuery`); el orden por defecto es alfabético por
 * `displayName` (default de presentación Fiado).
 */
export function LevelEntitiesTable({
    tenantId,
    level,
    levelDisplayName,
    requireExternalId = false,
    parentId,
}: Props) {
    const [createOpen, setCreateOpen] = useState(false);
    const [globalFilter, setGlobalFilter] = useState('');
    // Lo más nuevo arriba: la entidad recién creada abre la tabla. El alfabético sigue a un clic.
    const [sorting, setSorting] = useState<SortingState>([{ id: 'createdAt', desc: true }]);

    const { data: items, isLoading } = useLevelEntities(tenantId, level, parentId);

    const baseColumns = useMemo<ColumnDef<LevelEntityListItemLocal>[]>(
        () => [
            {
                accessorKey: 'displayName',
                header: ({ column }) => <SortableHeader column={column} title="Nombre" />,
            },
            {
                id: 'id',
                accessorFn: (row) => row.id,
                header: ({ column }) => <SortableHeader column={column} title="ID" />,
                // DEC-013: el id es info operativa (el "Nombre" ya está en su columna). No se pinta
                // crudo: CopyableId lo acorta + copiable, sin disparar la regla anti-UUID.
                cell: (info) => (
                    <CopyableId
                        label={`ID de ${levelDisplayName}`}
                        value={info.getValue() as string}
                    />
                ),
            },
            {
                accessorKey: 'createdAt',
                header: ({ column }) => <SortableHeader column={column} title="Creado" />,
                enableGlobalFilter: false,
                cell: (info) => {
                    const iso = info.getValue() as string | undefined;
                    return iso ? formatDateMx(iso) : '—';
                },
            },
        ],
        [levelDisplayName],
    );

    const columns = useMemo<ColumnDef<LevelEntityListItemLocal>[]>(
        () => [
            ...baseColumns,
            {
                id: 'actions',
                header: 'Acciones',
                enableSorting: false,
                enableGlobalFilter: false,
                cell: (info) => (
                    <LevelEntityRowActions
                        entity={info.row.original}
                        tenantId={tenantId}
                        level={level}
                        levelDisplayName={levelDisplayName}
                    />
                ),
            },
        ],
        [baseColumns, tenantId, level, levelDisplayName],
    );

    // useMemo OBLIGATORIO: useReactTable compara `data` por referencia.
    const rows = useMemo(() => items ?? [], [items]);
    const table = useReactTable({
        data: rows,
        columns,
        state: { globalFilter, sorting },
        onGlobalFilterChange: setGlobalFilter,
        onSortingChange: setSorting,
        globalFilterFn,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: { pagination: { pageSize: 25 } },
    });

    // Spinner del área de contenido mientras la lista del nivel carga. `isLoading` (no `isPending`)
    // porque la query del hook es condicional (`enabled: !!tenantId`).
    if (isLoading) return <ContentLoading />;

    return (
        <div className="space-y-3">
            <div className="flex justify-end">
                <Gate permission="tenant.level.manage" scope="TENANT" scopeRef={tenantId}>
                    <Button
                        onClick={() => {
                            setCreateOpen(true);
                        }}
                    >
                        Crear {levelDisplayName.toLowerCase()}
                    </Button>
                </Gate>
            </div>

            {createOpen && (
                <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-16">
                    <button
                        type="button"
                        aria-label="Cerrar"
                        className="fixed inset-0 bg-black/50"
                        onClick={() => {
                            setCreateOpen(false);
                        }}
                    />
                    <div className="relative w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg">
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="text-lg font-semibold">
                                Crear {levelDisplayName.toLowerCase()}
                            </h2>
                            <button
                                type="button"
                                aria-label="Cerrar"
                                className="text-muted-foreground hover:text-foreground"
                                onClick={() => {
                                    setCreateOpen(false);
                                }}
                            >
                                ✕
                            </button>
                        </div>
                        <LevelEntityForm
                            tenantId={tenantId}
                            level={level}
                            levelDisplayName={levelDisplayName}
                            requireExternalId={requireExternalId}
                            parentId={parentId}
                            onDone={() => {
                                setCreateOpen(false);
                            }}
                        />
                    </div>
                </div>
            )}

            <DataTableToolbar value={globalFilter} onChange={setGlobalFilter} />

            <Table>
                <TableHeader>
                    {table.getHeaderGroups().map((hg) => (
                        <TableRow key={hg.id}>
                            {hg.headers.map((h) => (
                                <TableHead key={h.id} className="text-center">
                                    {flexRender(h.column.columnDef.header, h.getContext())}
                                </TableHead>
                            ))}
                        </TableRow>
                    ))}
                </TableHeader>
                <TableBody>
                    {table.getRowModel().rows.map((r) => (
                        <TableRow key={r.id}>
                            {r.getVisibleCells().map((c) => (
                                <TableCell key={c.id} className="text-center align-middle">
                                    {flexRender(c.column.columnDef.cell, c.getContext())}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <DataTablePagination table={table} />
        </div>
    );
}
